import React, { useEffect, useMemo, useState } from 'react';
import { Flex, useDisclosure } from '@withjoy/joykit';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { AccommodationPlaceType, GuestTravelMapComponentFragment, TravelCurrencyCode } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAccommodationsData } from './hooks/useAccommodationsData';
import { ExploreOtherOptions } from './components/ExploreOtherOptions';
import { AccommodationsTileV2, AccommodationsTileV2Skeleton } from './components/AccommodationsV2/AccommodationsTileV2';
import { AccommodationsTileV2Favorite, AccommodationsTileV2FavoriteSkeleton } from './components/AccommodationsV2/AccommodationsTileV2Favorite';
import { AccommodationCombined, AccommodationCombinedType, AccommodationSourceType } from './components/Accommodations/Accommodation.types';
import { AccommodationsMapV2, AccommodationsMapV2Skeleton } from './components/AccommodationsMapV2';
import { useCustomPageTelemetry } from '../../Custom.telemetry';
import { JoyInteractableMapProvider } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { RedirectWithSafeLink } from '@apps/guest/components/RedirectWithSafeLink';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { useEventDisplayName, useEventInfo } from '@shared/utils/eventInfo';
import { isInIframe } from '@shared/utils/isInIframe';
import { useTravelPlacesPricingAndBookingDetailsQuery } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { useTravelPlacesPriceComparisonQuery } from '@shared/hooks/useTravelPlacesPriceComparison';
import { useTravelReservationsSummary } from './hooks/useTravelReservationsSummary';
import { useAccommodationPlacePhotos } from '@shared/utils/accommodations';
import { useReminderModalTrigger } from './hooks/useReminderModalTrigger';
import { ReminderModal } from '@shared/components/ReminderModal';
import { AccommodationsHotelsCount } from './components/AccommodationsHotelsCount';
import AccommodationsConsoleDialog from '@apps/accommodations';
import { AccommodationsConsoleConfigProvider } from '@apps/accommodations/AccommodationsConsoleConfigContext';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useMapReset } from './hooks/useMapReset';
import { AccommodationsExpandedLayout } from './components/AccommodationsExpandedLayout';
import { pxToRem } from '@withjoy/joykit/theme';
import { HotelPricingData, useRegisterHotelPrices } from '@shared/components/DiscountsProvider/hotels/hooks/useRegisterHotelPrices';
import { AccommodationCurrencyProvider } from './contexts/AccommodationCurrencyContext';
import { HotelTileCardSkeleton } from '@shared/components/HotelTileCard/HotelTileCard.skeleton';

interface AccommodationsProps {
  pageId: string;
  eventId: string;
  pageSlug: string;
  eventHandle: string;
  pageTitle?: string;
  subTitle?: string | null;
  isBrannanLayout?: boolean;
  travelMapComponentData: GuestTravelMapComponentFragment;
}

export const AccommodationsV2Inner: React.FC<AccommodationsProps> = ({ pageSlug, eventId, eventHandle, pageTitle, isBrannanLayout, travelMapComponentData }) => {
  const { isOpen: isAccommodationsConsoleOpen, onOpen: onAccommodationsConsoleOpen, onClose: onAccommodationsConsoleClose } = useDisclosure();
  const [isTravelAccommodationsConsoleV2Enabled] = useIsFeatureEnabled('travelAccommodationsConsoleV2');
  const [isAccommodationsExpandedMapEnabled] = useIsFeatureEnabled('accommodationsExpandedMap');
  const [safeLink, setSafeLink] = useState<string | undefined>();
  const [selectedAccommodation, setSelectedAccommodation] = useState<AccommodationCombined | undefined>(undefined);
  const [locationBeingHovered, setLocationBeingHovered] = useState<string | undefined>(undefined);
  // typical way to check if the page is in preview mode
  const isPreviewMode = isInIframe();
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isGridView = isBrannanLayout && !isMobile;

  const { pageColor, artBackgroundColor, brannanInnerFrameBorder, themeId } = useColorPaletteProvider();
  // A full-width map gives blank-theme events a cleaner, more immersive layout without clashing with any decorative design elements.
  // We're testing this to see if a more engaging, streamlined map view improves user interaction and accommodation booking conversions.
  const isFullWidth = Boolean(isAccommodationsExpandedMapEnabled && isBrannanLayout && pageColor === artBackgroundColor && !brannanInnerFrameBorder && themeId === 'blank');
  const customMapStyles = pageColor ? { paddingTop: isBrannanLayout ? 10 : 0, paddingBottom: isBrannanLayout ? 10 : 0 } : {};
  const customMapWrapperStyles = pageColor ? { marginTop: 5 } : {};

  const enableHoverShadow = isFullWidth ? pageColor === '#fff' && artBackgroundColor === '#fff' : pageColor === '#fff';

  const { loading, showDefaultData, destination, eventPhoto, accommodations: accommodationsData, eventCurrency } = useAccommodationsData(eventId, travelMapComponentData);

  const { finalizedEventDate, loading: eventInfoLoading } = useEventInfo();
  const eventDisplayName = useEventDisplayName();
  const { showReminderModal, handleReminderModalClose } = useReminderModalTrigger(finalizedEventDate);

  const { resetMapToShowAllMarkers } = useMapReset(accommodationsData, destination);

  const [isAccommodationLinkInterceptionEnabled] = useIsFeatureEnabled('accommodationLinkInterceptionEnabled');
  const [genericHotelTileEnabled] = useIsFeatureEnabled('genericHotelTile');

  const accommodationPlaceIds = accommodationsData.filter(accommodation => accommodation.type !== AccommodationCombinedType.Roomblock).map(accommodation => accommodation.id);
  const joyPlaceIds = accommodationsData
    .filter(
      accommodation =>
        accommodation.joyPlaceId && accommodation.accommodationType && [AccommodationPlaceType.hotel, AccommodationPlaceType.custom].includes(accommodation.accommodationType),
    )
    .map(accommodation => accommodation.joyPlaceId)
    .filter(Boolean);

  const favoriteAccommodation = accommodationsData.find(roomBlock => roomBlock.isFavorite);
  const { primaryAccommodation, nonPrimaryAccommodations } = useMemo(() => {
    const primaryAccommodation = isGridView && favoriteAccommodation ? favoriteAccommodation : null;
    const nonPrimaryAccommodations = primaryAccommodation ? accommodationsData?.filter(accommodation => accommodation.id !== favoriteAccommodation?.id) : accommodationsData;
    return {
      primaryAccommodation,
      nonPrimaryAccommodations,
    };
  }, [favoriteAccommodation, accommodationsData, isGridView]);

  const telemetry = useCustomPageTelemetry();

  const { getReservationsCountData } = useTravelReservationsSummary(joyPlaceIds as string[]);
  const { accommodationPropertyPhotos } = useAccommodationPlacePhotos(joyPlaceIds as string[]);

  const onMapItemClicked = useEventCallback((hotel: AccommodationCombined) => {
    if (hotel) {
      const telemetryHotelInfo = {
        hotelName: hotel.displayName || '',
        isHotelPreferred: hotel.isFavorite || false,
        hotelPrice: hotel.pricePerNight || undefined,
        hotelStrikeThroughPrice: hotel.strikeoutPricePerNight || undefined,
        type: hotel.type,
      };
      telemetry.mapItemClicked('accommodations', telemetryHotelInfo);
      setSelectedAccommodation(hotel);
      onAccommodationsConsoleOpen?.();
    }
  });

  const [isInterceptEnabled] = useIsFeatureEnabled({ consentKey: 'websiteAllowHotelLinkIntercept' });

  // Show map if have at least one venue or hotel with latitude or longitude
  const shouldShowMap = useMemo(() => accommodationsData?.find(accommodation => accommodation.latitude && accommodation.longitude), [accommodationsData]);
  const isExpandedLayout = isAccommodationsExpandedMapEnabled && isBrannanLayout && !isMobile;

  const renderMap = useMemo(() => {
    return (
      <SkeletonGroup
        isReady={!loading}
        placeholder={
          <AccommodationsMapV2Skeleton
            isBrannanLayout={isBrannanLayout}
            isExpandedLayout={isExpandedLayout}
          />
        }
        width="100%"
        height={isExpandedLayout ? '100%' : 'auto'}
        paddingRight={isBrannanLayout ? { _: 0, md: isAccommodationsExpandedMapEnabled ? 0 : 6 } : 0}
      >
        {shouldShowMap && (
          <AccommodationsMapV2
            key={`accommodations-guest-map-${destination.latitude}-${destination.longitude}`}
            showFullScreenMap={false}
            venueInfo={destination}
            accommodationsCombined={accommodationsData ?? []}
            pageTitle={pageTitle}
            isBrannanLayout={isBrannanLayout}
            locationBeingHovered={locationBeingHovered}
            onAccommodationsConsoleOpen={onAccommodationsConsoleOpen}
            hideDirection
            onMapItemClick={onMapItemClicked}
          />
        )}
      </SkeletonGroup>
    );
  }, [
    loading,
    isBrannanLayout,
    isExpandedLayout,
    isAccommodationsExpandedMapEnabled,
    shouldShowMap,
    destination,
    accommodationsData,
    pageTitle,
    locationBeingHovered,
    onAccommodationsConsoleOpen,
    onMapItemClicked,
  ]);

  const modalsContainer = (
    <>
      <ReminderModal
        telemetry={telemetry}
        isOpen={showReminderModal && !isAccommodationsConsoleOpen}
        onClose={handleReminderModalClose}
        eventId={eventId}
        showCloseButton={false}
      />
      {isTravelAccommodationsConsoleV2Enabled && (
        <AccommodationsConsoleConfigProvider>
          <AccommodationsConsoleDialog
            isOpen={isAccommodationsConsoleOpen}
            onClose={() => {
              onAccommodationsConsoleClose();
              resetMapToShowAllMarkers();
            }}
            eventName={eventDisplayName}
            viewMode="overlay"
            selectedHotelId={selectedAccommodation?.joyPlaceId || ''}
            eventHandle={eventHandle}
          />
        </AccommodationsConsoleConfigProvider>
      )}
      {!eventInfoLoading && !isPreviewMode && !loading && (
        <RequestAccommodationsPricing
          accommodationPlaceIds={accommodationPlaceIds}
          joyPlaceIds={joyPlaceIds as string[]}
          accommodations={accommodationsData}
          eventCurrency={eventCurrency}
        />
      )}
    </>
  );

  if (isExpandedLayout) {
    return (
      <AccommodationCurrencyProvider eventCurrency={eventCurrency}>
        <AccommodationsExpandedLayout
          loading={loading}
          renderMap={renderMap}
          primaryAccommodation={primaryAccommodation}
          nonPrimaryAccommodations={nonPrimaryAccommodations}
          destination={destination}
          isInterceptEnabled={isInterceptEnabled}
          getReservationsCountData={getReservationsCountData}
          accommodationPropertyPhotos={accommodationPropertyPhotos}
          showDefaultData={showDefaultData}
          setLocationBeingHovered={setLocationBeingHovered}
          onAccommodationsConsoleOpen={onAccommodationsConsoleOpen}
          isAccommodationLinkInterceptionEnabled={isAccommodationLinkInterceptionEnabled}
          travelMapComponentData={travelMapComponentData}
          pageSlug={pageSlug}
          eventPhoto={eventPhoto}
          enableHoverShadow={enableHoverShadow}
          safeLink={safeLink}
          setSafeLink={setSafeLink}
          isFullWidth={isFullWidth}
        />
        {modalsContainer}
      </AccommodationCurrencyProvider>
    );
  }

  return (
    <AccommodationCurrencyProvider eventCurrency={eventCurrency}>
      <Flex width="100%">
        <Flex
          flexDirection="column"
          alignItems="center"
          width="100%"
          rowGap={{ _: 7, sm2: isBrannanLayout ? 7 : 8 }}
          {...customMapWrapperStyles}
        >
          <Flex
            flexDirection="column"
            alignItems="center"
            width="100%"
            rowGap={{ _: 0, sm2: isBrannanLayout ? pxToRem(40) : 0 }}
            paddingTop={{ _: 8, sm2: isBrannanLayout ? 5 : 8 }}
            {...customMapStyles}
          >
            {(primaryAccommodation || !isGridView) && (
              <Flex
                width="100%"
                paddingX={{ _: 0, sm2: isBrannanLayout ? 7 : 0 }}
              >
                <Flex
                  width="100%"
                  display={isGridView ? 'grid' : 'flex'}
                  flexDirection="column"
                  gridTemplateColumns="1fr 2fr"
                  marginBottom={pxToRem(10)}
                  gap={{ _: pxToRem(32), sm2: isBrannanLayout ? pxToRem(32) : pxToRem(32) }}
                >
                  {shouldShowMap && renderMap}
                  <SkeletonGroup
                    isReady={!loading}
                    paddingX={isMobile ? pxToRem(16) : 0}
                    marginBottom={!isBrannanLayout ? pxToRem(10) : 0}
                    placeholder={<AccommodationsTileV2FavoriteSkeleton />}
                  >
                    {primaryAccommodation && (
                      <AccommodationsTileV2Favorite
                        accommodation={primaryAccommodation}
                        isFirst={true}
                        isLast={false}
                        locationInfo={destination}
                        enableHoverShadow={enableHoverShadow}
                        isInterceptEnabled={isInterceptEnabled}
                        reservationCount={getReservationsCountData(primaryAccommodation.joyPlaceId) ?? 0}
                        accommodationPropertyPhotos={
                          accommodationPropertyPhotos?.searchAccommodationProperties?.properties?.find(property => property.joyPlaceId === primaryAccommodation.joyPlaceId)?.photos
                        }
                        showDefaultData={showDefaultData}
                        setLocationBeingHovered={setLocationBeingHovered}
                        from={AccommodationSourceType.HotelMapList}
                        onAccommodationsConsoleOpen={onAccommodationsConsoleOpen}
                        isAccommodationLinkInterceptionEnabled={isAccommodationLinkInterceptionEnabled}
                      />
                    )}
                  </SkeletonGroup>
                </Flex>
              </Flex>
            )}
            <Flex
              width="100%"
              paddingX={{ _: isBrannanLayout ? 3 : 0, sm2: isBrannanLayout ? 7 : 0 }}
            >
              <Flex
                width="100%"
                flexDirection="column"
                gap={7}
              >
                <SkeletonGroup
                  isReady={!loading}
                  placeholder={
                    genericHotelTileEnabled ? (
                      <>
                        <HotelTileCardSkeleton />
                        <HotelTileCardSkeleton />
                        <HotelTileCardSkeleton />
                      </>
                    ) : (
                      <>
                        <AccommodationsTileV2Skeleton />
                        <AccommodationsTileV2Skeleton />
                        <AccommodationsTileV2Skeleton />
                      </>
                    )
                  }
                  width="100%"
                  display={isGridView ? 'grid' : 'flex'}
                  flexDirection="column"
                  gridTemplateColumns={{
                    _: 'repeat(1, minmax(0px, 1fr))',
                    sm2: 'repeat(2, minmax(0px, 1fr))',
                    md: 'repeat(3, minmax(0px, 1fr))',
                  }}
                  gap={{ _: pxToRem(32), sm2: pxToRem(32), md: pxToRem(32) }}
                  paddingX={isMobile ? pxToRem(10) : 0}
                >
                  {shouldShowMap && !primaryAccommodation && isGridView && renderMap}
                  {nonPrimaryAccommodations && nonPrimaryAccommodations?.length > 0 && (
                    <>
                      {nonPrimaryAccommodations.map((accommodation, idx) => {
                        return (
                          <AccommodationsTileV2
                            accommodation={accommodation}
                            key={`${accommodation.id}-${idx}`}
                            reservationCount={getReservationsCountData(accommodation.joyPlaceId) ?? 0}
                            // In Brannan layouts, the primary accommodation is rendered separately (not in this map).
                            // When a primary accommodation exists, we add 1 to the index to account for it in the total count.
                            // This ensures accurate numbering: if primary exists, these start at 2, 3, 4...; otherwise 1, 2, 3...
                            idx={primaryAccommodation ? idx + 1 : idx}
                            locationInfo={destination}
                            enableHoverShadow={enableHoverShadow}
                            accommodationPropertyPhotos={
                              accommodationPropertyPhotos?.searchAccommodationProperties?.properties?.find(property => property.joyPlaceId === accommodation.joyPlaceId)?.photos
                            }
                            showDefaultData={showDefaultData}
                            isInterceptEnabled={isInterceptEnabled}
                            setLocationBeingHovered={setLocationBeingHovered}
                            from={AccommodationSourceType.AccomPageGrid}
                            onAccommodationsConsoleOpen={onAccommodationsConsoleOpen}
                            isAccommodationLinkInterceptionEnabled={isAccommodationLinkInterceptionEnabled}
                          />
                        );
                      })}
                    </>
                  )}
                </SkeletonGroup>
              </Flex>
            </Flex>
          </Flex>
          {!travelMapComponentData.hideExploreCTA && <ExploreOtherOptions pageSlug={pageSlug} />}
          <RedirectWithSafeLink
            href={safeLink}
            skipTelemetry
            onClose={() => {
              setSafeLink(undefined);
            }}
          />
        </Flex>
        {modalsContainer}
      </Flex>
    </AccommodationCurrencyProvider>
  );
};

export const AccommodationsV2: React.FC<AccommodationsProps> = props => {
  return (
    <JoyInteractableMapProvider>
      <AccommodationsV2Inner {...props} />
    </JoyInteractableMapProvider>
  );
};

interface RequestAccommodationsPricingProps
  extends Readonly<{
    joyPlaceIds: string[];
    accommodationPlaceIds: string[];
    accommodations: AccommodationCombined[];
    eventCurrency?: TravelCurrencyCode | null;
  }> {}

const RequestAccommodationsPricing = ({ accommodationPlaceIds, joyPlaceIds, accommodations, eventCurrency }: RequestAccommodationsPricingProps) => {
  const { registerHotelPrices } = useRegisterHotelPrices();
  const { data: travelPlacesPricingAndBookingDetailsData, loading: travelPlacesPricingAndBookingDetailsLoading } = useTravelPlacesPricingAndBookingDetailsQuery(
    accommodationPlaceIds,
    'accommodationPlace',
    false,
    {},
    false,
    eventCurrency,
  );
  const { data: travelPlacesPriceComparisonData, loading: travelPlacesPriceComparisonLoading } = useTravelPlacesPriceComparisonQuery(joyPlaceIds);

  const isLoading = travelPlacesPricingAndBookingDetailsLoading || travelPlacesPriceComparisonLoading;

  const priceDataList = useMemo(
    () => travelPlacesPricingAndBookingDetailsData?.travelPlacesPricingAndBookingDetails.accommodationPlaces ?? [],
    [travelPlacesPricingAndBookingDetailsData],
  );
  const travelPlaces = useMemo(() => travelPlacesPriceComparisonData?.travelRateComparisonsForTravelPlaces ?? [], [travelPlacesPriceComparisonData]);

  useEffect(() => {
    if (!isLoading) {
      const prices: ReadonlyArray<HotelPricingData> = priceDataList
        .filter(data => data.pricingInfo?.nightlyTotalRate)
        .map(data => ({
          advertisedPrice: data.pricingInfo!.nightlyTotalRate.valueInMinorUnits,
          strikeoutPrice: data.pricingInfo!.strikeoutNightlyTotalRate?.valueInMinorUnits ?? null,
        }));
      registerHotelPrices(prices);
    }
  }, [isLoading, priceDataList, registerHotelPrices]);

  return isLoading ? null : (
    <AccommodationsHotelsCount
      accommodations={accommodations}
      priceDataList={priceDataList}
      travelPlaces={travelPlaces}
    />
  );
};
